import { useState, useRef, useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Document, Page, pdfjs } from "react-pdf";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { Skeleton } from "@libs/zapi-ui/ds/components/skeleton";
import $api from "@/zapi-frontend/shared/api/axios";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

type TPdfPreviewProps = {
  pdfUrl: string | null;
  slideTitles?: string[];
  initialPage?: number;
  onPageChange?: (page: number) => void;
  isPageLoading?: boolean;
};

const MAX_RETRIES = 5;
const MAX_PAGE_WIDTH = 868;
const PAGE_ASPECT_RATIO = 489 / 1024;

const presignedUrlCache = new Map<string, string>();

function PreviewSkeleton({ label }: { label: string }) {
  return (
    <div className="flex h-full w-full" aria-busy="true">
      <span className="sr-only">{label}</span>
      <div className="flex w-[220px] min-w-[220px] flex-col gap-1 p-2">
        {Array.from({ length: 5 }, (_, index) => (
          <div key={index} className="flex items-start gap-2 p-3">
            <Skeleton className="h-4 w-4 shrink-0" />
            <Skeleton className="aspect-210/120 min-w-0 flex-1 rounded" />
          </div>
        ))}
      </div>
      <div className="flex min-w-0 flex-1 flex-col items-center gap-2 overflow-hidden py-2">
        <Skeleton className="aspect-[1024/489] w-full max-w-[868px] shrink-0 rounded-none" />
        <Skeleton className="aspect-[1024/489] w-full max-w-[868px] shrink-0 rounded-none" />
      </div>
    </div>
  );
}

type TShownDeck = { projectId: string; url: string };

// A rebuilt deck arrives under a new link. The deck on screen stays until the new one has drawn
// the page the reader is on, so the preview never goes blank between the two.
export function PdfPreview(props: TPdfPreviewProps) {
  const { t } = useTranslation("projectHome");
  const { pdfUrl, initialPage = 1, onPageChange, isPageLoading } = props;
  const { id: projectId = "" } = useParams();

  const cacheKey = `${projectId}:${pdfUrl}`;
  const cachedUrl = pdfUrl ? presignedUrlCache.get(cacheKey) : undefined;

  const [shownDeck, setShownDeck] = useState<TShownDeck | null>(cachedUrl ? { projectId, url: cachedUrl } : null);
  const [nextUrl, setNextUrl] = useState<string | null>(null);
  const [isUrlLoading, setIsUrlLoading] = useState(!cachedUrl);
  const shown = shownDeck?.projectId === projectId ? shownDeck.url : null;
  const shownRef = useRef(shown);
  shownRef.current = shown;

  useEffect(() => {
    setNextUrl(null);
    if (!pdfUrl || !projectId) {
      setShownDeck(null);
      setIsUrlLoading(false);
      return;
    }

    const show = (url: string) => {
      if (shownRef.current && shownRef.current !== url) setNextUrl(url);
      else setShownDeck({ projectId, url });
    };

    const cached = presignedUrlCache.get(cacheKey);
    if (cached) {
      show(cached);
      setIsUrlLoading(false);
      return;
    }

    let cancelled = false;
    if (!shownRef.current) setIsUrlLoading(true);

    async function fetch() {
      try {
        const response = await $api.get<{ data: { url: string } }>(
          `/projects/${projectId}/presentations/download-url`,
          { params: { fileType: "pdf", recordHistory: "false" } }
        );
        if (!cancelled) {
          const url = response.data.data.url;
          presignedUrlCache.set(cacheKey, url);
          show(url);
        }
      } catch (error) {
        console.error("Failed to fetch presigned URL:", error);
        if (!cancelled && pdfUrl) show(pdfUrl);
      } finally {
        if (!cancelled) setIsUrlLoading(false);
      }
    }

    fetch();
    return () => {
      cancelled = true;
    };
  }, [pdfUrl, projectId, cacheKey]);

  const nextRef = useRef(nextUrl);
  nextRef.current = nextUrl;
  const promoteNext = useCallback(
    (url: string) => {
      if (nextRef.current !== url) return;
      setNextUrl(null);
      setShownDeck({ projectId, url });
    },
    [projectId]
  );

  if (isUrlLoading || isPageLoading) return <PreviewSkeleton label={t("presentation.loadingPresentation")} />;

  if (!pdfUrl) return null;
  // The link falls back to the stored one when it cannot be signed, so one always arrives.
  if (!shown) return <PreviewSkeleton label={t("presentation.loadingPresentation")} />;

  return (
    <div className="relative flex w-full h-full">
      {[shown, nextUrl]
        .flatMap((url) => (url ? [url] : []))
        .map((url) => (
          <PdfDeck
            key={url}
            file={url}
            initialPage={initialPage}
            isPending={url !== shown}
            onPageChange={url === shown ? onPageChange : undefined}
            onReady={promoteNext}
          />
        ))}
    </div>
  );
}

type TPdfDeckProps = {
  file: string;
  initialPage: number;
  // A deck loading behind the one on screen: drawn out of sight, and reported ready once the
  // reader's page has rendered.
  isPending: boolean;
  onPageChange?: (page: number) => void;
  onReady: (file: string) => void;
};

function PdfDeck({ file, initialPage, isPending, onPageChange, onReady }: TPdfDeckProps) {
  const { t } = useTranslation("projectHome");
  const [numPages, setNumPages] = useState<number>(0);
  const [selectedPage, setSelectedPage] = useState<number>(initialPage);
  const [isLoading, setIsLoading] = useState(true);
  const [retryKey, setRetryKey] = useState(0);

  const retryCountRef = useRef(0);
  const mainContainerRef = useRef<HTMLDivElement | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const [pageWidth, setPageWidth] = useState(MAX_PAGE_WIDTH);
  const setMainContainerRef = useCallback((el: HTMLDivElement | null) => {
    mainContainerRef.current = el;
    resizeObserverRef.current?.disconnect();
    resizeObserverRef.current = null;
    if (!el) return;
    const updateWidth = () => setPageWidth(Math.min(MAX_PAGE_WIDTH, Math.max(1, el.clientWidth - 8)));
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(el);
    resizeObserverRef.current = observer;
  }, []);
  const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const thumbnailRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const isProgrammaticScrollRef = useRef(false);
  const hasInitialScrolledRef = useRef(false);
  const rafRef = useRef(0);
  const onPageChangeRef = useRef(onPageChange);
  onPageChangeRef.current = onPageChange;
  const hasReportedReadyRef = useRef(false);
  const readyPage = Math.min(Math.max(initialPage, 1), Math.max(numPages, 1));

  const onPageRendered = (pageNum: number) => {
    if (pageNum !== readyPage || hasReportedReadyRef.current) return;
    hasReportedReadyRef.current = true;
    onReady(file);
  };

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setNumPages(numPages);
    setIsLoading(false);
    retryCountRef.current = 0;
  }

  function onDocumentLoadError(error: Error) {
    console.error("Error loading PDF:", error);
    if (retryCountRef.current < MAX_RETRIES) {
      retryCountRef.current += 1;
      setTimeout(() => setRetryKey((prev) => prev + 1), 1000);
    } else {
      setIsLoading(false);
    }
  }

  const handleThumbnailClick = useCallback((pageNum: number) => {
    isProgrammaticScrollRef.current = true;
    setSelectedPage(pageNum);
    onPageChangeRef.current?.(pageNum);

    const pageElement = pageRefs.current.get(pageNum);
    if (pageElement) {
      pageElement.scrollIntoView({ behavior: "smooth", block: "start" });
      setTimeout(() => {
        isProgrammaticScrollRef.current = false;
      }, 600);
    }
  }, []);

  useEffect(() => {
    if (numPages === 0) return;
    const el = thumbnailRefs.current.get(selectedPage);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [selectedPage, numPages]);

  useEffect(() => {
    if (numPages === 0 || hasInitialScrolledRef.current) return;
    hasInitialScrolledRef.current = true;

    if (initialPage <= 1) return;

    isProgrammaticScrollRef.current = true;

    const scrollToInitial = () => {
      const el = pageRefs.current.get(initialPage);
      if (el) el.scrollIntoView({ behavior: "auto", block: "start" });
    };

    const t1 = setTimeout(scrollToInitial, 0);
    const t2 = setTimeout(() => {
      scrollToInitial();
      isProgrammaticScrollRef.current = false;
    }, 300);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [numPages, initialPage]);

  useEffect(() => {
    const container = mainContainerRef.current;
    if (!container || numPages === 0) return;

    const onScroll = () => {
      if (isProgrammaticScrollRef.current) return;

      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        const containerRect = container.getBoundingClientRect();
        const containerCenter = containerRect.top + containerRect.height / 2;

        let closestPage = 1;
        let closestDistance = Infinity;

        for (let i = 1; i <= numPages; i++) {
          const el = pageRefs.current.get(i);
          if (!el) continue;

          const rect = el.getBoundingClientRect();
          const elCenter = rect.top + rect.height / 2;
          const distance = Math.abs(containerCenter - elCenter);

          if (distance < closestDistance) {
            closestDistance = distance;
            closestPage = i;
          }
        }

        setSelectedPage((prev) => {
          if (prev === closestPage) return prev;
          onPageChangeRef.current?.(closestPage);
          return closestPage;
        });
      });
    };

    container.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      container.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(rafRef.current);
    };
  }, [numPages]);

  return (
    <div
      className={cn("flex w-full h-full", isPending && "invisible absolute inset-0")}
      aria-hidden={isPending || undefined}
    >
      <div
        className={cn(
          "w-[220px] min-w-[220px] bg-white flex flex-col overflow-hidden shadow-200",
          isLoading && "hidden"
        )}
      >
        <div className="flex-1 overflow-y-auto custom-thin-scrollbar p-2 flex flex-col gap-1">
          <Document
            key={`thumb-${retryKey}`}
            file={file}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
          >
            {Array.from({ length: numPages }, (_, index) => {
              const pageNum = index + 1;
              const isSelected = selectedPage === pageNum;
              return (
                <div
                  key={`thumb-${pageNum}`}
                  data-testid={`pdf-preview-page-${pageNum}-item`}
                  ref={(el) => {
                    if (el) thumbnailRefs.current.set(pageNum, el);
                  }}
                  onClick={() => handleThumbnailClick(pageNum)}
                  className={cn(
                    "flex items-start gap-2 p-3 rounded-lg cursor-pointer transition-all",
                    isSelected ? "bg-[rgba(255,96,62,0.05)]" : "hover:bg-innoveraZinc-50"
                  )}
                >
                  <span
                    className={cn(
                      "text-[14px] leading-[22px] shrink-0 w-4",
                      isSelected ? "text-innoveraOrange-400" : "text-innoveraZinc-500"
                    )}
                  >
                    {pageNum}.
                  </span>
                  <div className="flex-1 min-w-0 aspect-210/120 rounded overflow-hidden">
                    <Page
                      pageNumber={pageNum}
                      width={168}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      loading={<Skeleton className="h-full w-full rounded-none" />}
                    />
                  </div>
                </div>
              );
            })}
          </Document>
        </div>
      </div>

      <div ref={setMainContainerRef} className="flex-1 min-w-0 overflow-y-auto custom-thin-scrollbar">
        {isLoading && <PreviewSkeleton label={t("presentation.loadingPresentation")} />}

        <Document
          key={`main-${retryKey}`}
          file={file}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={null}
          className={cn(isLoading && "hidden")}
        >
          <div className="flex flex-col items-center gap-2 mx-auto max-w-[868px] py-2">
            {Array.from({ length: numPages }, (_, index) => {
              const pageNum = index + 1;
              return (
                <div
                  key={`page-${pageNum}`}
                  ref={(el) => {
                    if (el) pageRefs.current.set(pageNum, el);
                  }}
                  data-page-num={pageNum}
                >
                  <Page
                    pageNumber={pageNum}
                    width={pageWidth}
                    renderTextLayer={true}
                    renderAnnotationLayer={true}
                    onRenderSuccess={() => onPageRendered(pageNum)}
                    loading={
                      <Skeleton
                        className="rounded-none"
                        style={{ width: pageWidth, height: Math.round(pageWidth * PAGE_ASPECT_RATIO) }}
                      />
                    }
                  />
                </div>
              );
            })}
          </div>
        </Document>
      </div>
    </div>
  );
}
